import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { ChannelDot } from '@/components/console/ChannelDot';
import { useT } from '@/i18n/labels';
import { classifyChannel, getConversationLabel, getConversationPreview, getCustomerDisplayName } from '@/utils/conversationLabel';
import { formatRelative } from '@/utils/formatters';
import { formatDraftAge, isDraftHeld } from '@/utils/draftReview';
import type { Conversation } from '@/types';

interface ConvRowProps {
  c: Conversation;
  active: boolean;
  onClick: () => void;
}

/** The topic line is one line; a first message longer than this is cut here before the stylesheet's ellipsis. */
const TOPIC_MAX = 90;

/** `formatRelative` throws on a time it cannot parse; a row must never take the list down with it. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return formatRelative(iso);
  } catch {
    return '';
  }
}

/**
 * The conversation's TOPIC - a short identifier of what it is about, drawn at the top of the row. In order: the name a
 * member of staff gave it (`subject`), the email's subject line, then the start of what the customer first wrote. Empty
 * when the row knows none of them; the row then simply has no topic line (it never invents one).
 */
function topicOf(c: Conversation): string {
  const flat = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
  const meta = c.metadata ?? {};
  const named = flat(c.subject) || (classifyChannel(c.channel) === 'email' ? flat(meta.emailSubject || meta.subject) : '');
  const text = named || flat(c.firstMessagePreview);
  return text.length > TOPIC_MAX ? text.substring(0, TOPIC_MAX - 1) + '…' : text;
}

/** A single conversation row of the folder list. */
export const ConvRow: React.FC<ConvRowProps> = ({ c, active, onClick }) => {
  const { t, lang } = useT();

  // Line 1: the topic. Line 2: the person. Line 3: the NEWEST message from any side (the server's `lastPreview`); an old row
  // has none, and then the row's own last message reads as a customer message, exactly as before. Bot and agent messages
  // carry a reply mark and who wrote them.
  const topic = topicOf(c);
  const person = getCustomerDisplayName(c) || getConversationLabel(c);
  // The same words twice (an email with no known sender, whose "name" IS its subject) are said once.
  const name = person === topic ? '' : person;
  const flatPreview = (c.lastPreview ?? '').replace(/\s+/g, ' ').trim();
  const previewText = flatPreview || getConversationPreview(c, 200);
  const previewSender = flatPreview ? (c.lastPreviewSender ?? 'customer') : 'customer';
  const agentName = (c.lastPreviewAgentName ?? '').trim();
  const previewWho =
    previewSender === 'bot'
      ? t('row_prev_bot')
      : previewSender === 'agent'
        ? agentName
          ? t('row_prev_agent_named', { name: agentName })
          : t('row_prev_agent')
        : null;

  // OP-582 - a draft awaiting review had NO presence in the list at all, so the only way to learn a customer was waiting was
  // to already know. This chip is the row-level half of the fix (the Drafts folder and the rail badge are the other two);
  // it is the one chip the row keeps, because it is a customer waiting on a human, not a label.
  const draft = c.pendingDraft;
  const draftAge = draft ? formatDraftAge(draft.createdAtUtc, lang) : null;
  const draftLabel = draft
    ? t(isDraftHeld(draft) ? 'draft_badge_held' : 'draft_badge_waiting', { time: draftAge ?? '' })
    : null;

  const time = relativeTime(c.lastActivityAt || c.lastMessageTimestamp);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={'ac-conv' + (active ? ' active' : '')}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-current={active ? 'true' : undefined}
      data-testid={`conv-row-${c.sessionId}`}
      data-active={active}
    >
      <Avatar name={person} seed={c.sessionId} size={44} className="ac-conv-av">
        <ChannelDot channel={c.channel} />
      </Avatar>
      {/* Three lines (operator 2026-11-03): the topic, with the unread count; the person's name and the time; then the
          newest message (one line, prefixed with a reply mark and who wrote it when it was the bot or an agent). The status,
          the tags and the owner chips are gone from the row: the folder, the tabs and the open conversation say those. */}
      <div className="ac-conv-body">
        <div className="ac-conv-top">
          {topic && (
            <span className="ac-conv-topic" data-testid={`conv-topic-${c.sessionId}`} dir="auto" title={topic}>
              {topic}
            </span>
          )}
          {c.unreadCount > 0 && (
            <span className="ac-unread" aria-label={String(c.unreadCount)}>
              {c.unreadCount}
            </span>
          )}
        </div>
        {/* The time sits on the name line, not beside the topic, so the topic gets the whole width of the row. */}
        <div className="ac-conv-name">
          {name && (
            <span className="ac-conv-name-text" data-testid={`conv-name-${c.sessionId}`} dir="auto">
              {name}
            </span>
          )}
          <span className="ac-conv-time" data-testid={`conv-time-${c.sessionId}`}>
            {time}
          </span>
        </div>
        {previewText && (
          <div className="ac-conv-prev" data-testid={`conv-preview-${c.sessionId}`} data-sender={previewSender} dir="auto">
            {previewWho && (
              <span className="ac-conv-prev-who" data-testid={`conv-preview-who-${c.sessionId}`}>
                <span aria-hidden="true">{'↩'}</span> {previewWho}{' '}
              </span>
            )}
            {previewText}
          </div>
        )}
        {draft && (
          <div className="ac-conv-chips">
            <span
              className="ac-draft-chip"
              data-testid={`conv-draft-badge-${c.sessionId}`}
              data-held={isDraftHeld(draft) ? 'true' : 'false'}
              title={draftLabel ?? undefined}
            >
              <Icon name="mail" size={12} />
              {draftLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
